import { Stack } from "@mui/material";
import { useEffect } from "react";
import { useLoaderData } from "react-router-dom";

import PaginationControlsV2 from "../../../global/PaginationControlsV2";
import LoadingIndicator from "../../Common/LoadingIndicator/LoadingIndicator";
import usePaymentPermissions from "../hooks/usePaymentPermissions";
import usePaymentsData from "../hooks/usePaymentsData";
import usePaymentsEventHandlers from "../hooks/usePaymentsEventHandlers";
import { usePaymentStore } from "../store/paymentStore";

import PaymentsAddButton from "./PaymentsAddButton";
import PaymentsDeleteModal from "./PaymentsDeleteModal";
import PaymentsEditModal from "./PaymentsEditModal";
import PaymentsTable from "./PaymentsTable";

export default function Payments() {
  const { id: entityId } = useLoaderData();
  const { canAddPayment, canEditPayment, canDeletePayment, canViewPayment } =
    usePaymentPermissions();
  const { reset, currentPage, data, loading, resLimit, resultCount, setCurrentPage } =
    usePaymentStore();
  const { fetchPaymentsData } = usePaymentsData({ entityId, enabled: canViewPayment });
  const {
    modalState,
    isDeleting,
    handleDelete,
    handlePageChange,
    handleOpenAddModal,
    handleOpenEditModal,
    handleOpenDeleteModal,
    handleCloseModal,
  } = usePaymentsEventHandlers({ fetchPaymentsData, setCurrentPage });

  useEffect(() => {
    reset();
  }, [reset]);

  if (!canViewPayment) {
    return null;
  }

  if (loading && !data.length) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <div className="flex h-full flex-col gap-4">
        <div className="flex w-full flex-row items-center justify-end">
          {canAddPayment && <PaymentsAddButton handleAdd={handleOpenAddModal} />}
        </div>
        <div className="relative flex flex-1 flex-col gap-2 overflow-y-auto">
          <PaymentsTable
            data={data}
            isLoading={loading}
            onDelete={handleOpenDeleteModal}
            onEdit={handleOpenEditModal}
            canEdit={canEditPayment}
            canDelete={canDeletePayment}
          />
        </div>
        {resultCount > resLimit && (
          <div className="relative flex w-full justify-center">
            <Stack alignItems="center">
              <PaginationControlsV2
                currentPage={currentPage}
                handleChange={handlePageChange}
                resultCount={resultCount}
                resultLimit={resLimit}
              />
            </Stack>
          </div>
        )}
      </div>
      <PaymentsEditModal
        openModal={modalState.mode === "add" || modalState.mode === "edit"}
        entityId={entityId}
        mode={modalState.mode}
        paymentData={modalState.payment}
        handleCloseModal={handleCloseModal}
        onSaveSuccess={fetchPaymentsData}
      />
      <PaymentsDeleteModal
        openModal={modalState.mode === "delete"}
        handleCloseModal={handleCloseModal}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
}
